<template>
  <div>
    <main-layout>
      <MainMenu />
      <v-container>
        <v-layout wrap class="pt-4">
          <v-flex xs12 sm12>
            <h1>{{ $t('seminar_management') }}</h1>
          </v-flex>
          <v-flex xs12 sm6>
            <importxlsx :noraw="true" @onChange="handleImportSeminar"></importxlsx>
            <v-btn dark outline color="primary" @click="downloadImportTemplate">
              <em class="fas fa-download" />&nbsp;Download template
            </v-btn>
          </v-flex>
          <v-flex xs12 sm6 class="text-sm-right text-xs-right">
            <v-btn dark depressed color="primary" @click="showAdvancedSearch = !showAdvancedSearch">
              <v-icon>search</v-icon>
              &nbsp;
              {{ $t('advanced search') }}&nbsp;
              <em v-if="!showAdvancedSearch" class="fas fa-angle-down" />
              <em v-else class="fas fa-angle-up" />
            </v-btn>
            <v-btn dark outline color="primary" :loading="exportLoading" @click="handleExportXlsx">
              <em class="fas fa-file-export" />&nbsp;{{ $t('export') }}
            </v-btn>
          </v-flex>
          <v-flex>
            <div
              v-if="showAdvancedSearch"
              class="mt-2 mb-4 py-2"
              style="border: 1px solid #eee; border-radius: 10px; position: relative"
            >
              <v-layout wrap class="form-control">
                <v-flex xs12 sm4 px-2>
                  <div class="caption">Seminar ID</div>
                  <input v-model="filter.seminar_id" type="text" placeholder="Seminar ID" />
                </v-flex>
                <v-flex xs12 sm4 px-2>
                  <div class="caption">Seminar Name</div>
                  <input v-model="filter.seminar_name" type="text" placeholder="Seminar Name" />
                </v-flex>
                <v-flex xs12 sm4 px-2>
                  <div class="caption">Date From</div>
                  <DatepickerCustomDialog v-model="filter.seminar_date_from">
                    <template #default="{ computedDate }">
                      <v-text-field
                        class="default-input"
                        outline
                        :value="computedDate"
                        readonly
                        placeholder="DD/MM/YYYY"
                        type="text"
                        clearable
                        dense
                        hide-details
                        @click:clear="filter.seminar_date_from = ''"
                      ></v-text-field>
                    </template>
                  </DatepickerCustomDialog>
                </v-flex>
                <v-flex xs12 sm4 px-2>
                  <div class="caption">Date To</div>
                  <DatepickerCustomDialog v-model="filter.seminar_date_to">
                    <template #default="{ computedDate }">
                      <v-text-field
                        class="default-input"
                        outline
                        :value="computedDate"
                        readonly
                        placeholder="DD/MM/YYYY"
                        type="text"
                        clearable
                        dense
                        hide-details
                        @click:clear="filter.seminar_date_to = ''"
                      ></v-text-field>
                    </template>
                  </DatepickerCustomDialog>
                </v-flex>
                <v-flex xs12 sm4 px-2>
                  <div class="caption">Start Time</div>
                  <time-picker
                    v-model="filter.start_time"
                    class="custom-time-picker"
                    full-width
                    :minute-interval="5"
                    hide-disabled-minutes
                  ></time-picker>
                </v-flex>
                <v-flex xs12 sm4 px-2>
                  <div class="caption">End Time</div>
                  <time-picker
                    v-model="filter.end_time"
                    class="custom-time-picker"
                    full-width
                    :minute-interval="5"
                    hide-disabled-minutes
                  ></time-picker>
                </v-flex>
                <v-flex xs12 sm4 px-2>
                  <div class="caption">Location</div>
                  <input v-model="filter.location" type="text" placeholder="Location" />
                </v-flex>

                <v-flex xs12 sm4 px-2 pt-2>
                  <div class="caption">Status</div>
                  <v-radio-group
                    v-model="filter.seminar_status"
                    color="primary"
                    row
                    class="text-xs-center mt-0"
                  >
                    <v-radio color="primary" :label="$t('All')" value="all"></v-radio>
                    <v-radio color="primary" :label="$t('Active')" :value="true"></v-radio>
                    <v-radio color="primary" :label="$t('Inactive')" :value="false"></v-radio>
                  </v-radio-group>
                </v-flex>

                <v-flex xs12 sm4 class="text-xs-right text-sm-center">
                  <v-btn outline color="grey" @click="handleClearSearch">{{
                    $t('Clear Search')
                  }}</v-btn>
                  <v-btn depressed color="primary" @click="handleSearch">
                    <em class="fas fa-search mr-1" />
                    {{ $t('Search') }}
                  </v-btn>
                </v-flex>
              </v-layout>
            </div>
          </v-flex>
        </v-layout>
        <v-data-table
          hide-actions
          :headers="headers"
          :items="dataItems"
          :pagination.sync="pagination"
          class="elevation-1 mt-3"
        >
          <template slot="headerCell" slot-scope="props">
            <HeaderTableWithSort
              :headers="props.header"
              :sort-by.sync="sortBy"
              :order-by.sync="orderBy"
              @onSort="handleChangeSort"
            ></HeaderTableWithSort>
          </template>
          <template #items="props">
            <tr>
              <td>
                <toggle-button
                  :value="props.item.seminar_status"
                  :sync="true"
                  :color="$vuetify.theme.success"
                  :labels="{ checked: 'ON', unchecked: 'OFF' }"
                  style="display: inline-block"
                  @change="
                    handleChangeStatus(props.item.seminar_id, props.item.seminar_status)
                    props.item.seminar_status = !props.item.seminar_status
                  "
                />
              </td>
              <td>{{ props.item.seminar_id || '-' }}</td>
              <td>
                <span
                  class="cursor-pointer primary--text"
                  @click="handleRedirectToBooking(props.item.seminar_id)"
                  >{{ props.item.seminar_name || '-' }}</span
                >
              </td>
              <td>{{ props.item.description || '-' }}</td>
              <td>{{ props.item.details || '-' }}</td>
              <td>{{ props.item.seminar_date | DateFormat('DD/MM/YYYY') }}</td>
              <td>
                {{ getTimeFormat(props.item.start_time) }} -
                {{ getTimeFormat(props.item.end_time) }}
              </td>
              <td>{{ props.item.location || '-' }}</td>
              <td>
                <v-btn flat icon color="primary" @click="handleShowImage(props.item.seminar_pic)">
                  <v-icon>image_search</v-icon>
                </v-btn>
              </td>
              <td>
                <span class="primary--text">{{ props.item.seat_count }}</span
                >/{{ props.item.seats }}
              </td>
              <td>
                <v-btn
                  outline
                  fab
                  small
                  color="primary"
                  @click="handleDeleteSeminar(props.item.seminar_id)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
        <div class="mt-2">
          <v-pagination
            v-model="pageNumber"
            :total-visible="6"
            :length="pageLength"
            @input="handleChangePage"
          ></v-pagination>
        </div>

        <v-dialog v-model="showImageDialog" persistent width="500px">
          <v-card class="pa-3">
            <template v-if="selectedImage">
              <v-img :src="selectedImage" aspect-ratio="1" class="grey lighten-2">
                <template #placeholder>
                  <v-layout fill-height align-center justify-center ma-0>
                    <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                  </v-layout>
                </template>
              </v-img>
            </template>

            <div class="text-xs-center mt-3">
              <v-btn
                depressed
                color="primary"
                @click="
                  showImageDialog = false
                  selectedImage = null
                "
              >
                {{ $t('close') }}
              </v-btn>
            </div>
          </v-card>
        </v-dialog>
      </v-container>
    </main-layout>
  </div>
</template>

<script>
import mainAiraApi from '@/api/aira.js'
import MainMenu from './MainMenu.vue'
import { mapGetters } from 'vuex'
import * as XLSX from 'xlsx'
import { handleResponse, handleApiError } from '@/api/util'
import HeaderTableWithSort from '@/components/elements/HeaderTableWithSort.vue'
import { ORDER_BY } from '@/constants/orderBy.js'
import { ToggleButton } from 'vue-js-toggle-button'
import importxlsx from '@/components/elements/importxlsx.vue'
import dayjs from 'dayjs'
import DatepickerCustomDialog from '@/components/elements/DatepickerCustomDialog.vue'
// import CustomTimepicker from '@/components/elements/CustomTimepicker2.vue'
import TimePicker from '@/components/elements/TimePicker.vue'

export default {
  components: {
    MainMenu,
    HeaderTableWithSort,
    ToggleButton,
    importxlsx,
    DatepickerCustomDialog,

    TimePicker
  },
  data() {
    return {
      showAdvancedSearch: false,
      exportLoading: false,
      limit: 10,
      offset: 0,
      pagination: { rowsPerPage: -1, sortBy: '' },
      total: 0,
      pagetmp: 1,
      pageNumber: 1,
      dataItems: [],
      headers: [
        { text: 'Status', value: 'seminar_status', sortable: false, customSort: false },
        {
          text: 'Seminar ID',
          value: 'seminar_id',
          sortable: false,
          customSort: true,
          hint: 'ระบุตัวอักษรและตัวเลข'
        },
        {
          text: 'Seminar Name',
          value: 'seminar_name',
          sortable: false,
          customSort: false,
          hint: 'จำกัด 100 ตัวอักษร'
        },
        {
          text: 'Description',
          value: 'description',
          sortable: false,
          customSort: false,
          hint: 'จำกัด 100 ตัวอักษร'
        },
        {
          text: 'Detail',
          value: 'details',
          sortable: false,
          customSort: false,
          hint: 'จำกัด 700 ตัวอักษร'
        },
        {
          text: 'Date',
          value: 'seminar_date',
          sortable: false,
          customSort: true,
          hint: 'ระบุรูปแบบ DD/MM/YYYY เท่านั้น'
        },
        {
          text: 'Time',
          value: 'start_time',
          sortable: false,
          customSort: false,
          hint: 'ระบุรูปแบบ 00:00 เท่านั้น'
        },
        {
          text: 'Location',
          value: 'location',
          sortable: false,
          customSort: false,
          hint: 'จำกัด 150 ตัวอักษร'
        },
        {
          text: 'Picture',
          value: 'seminar_pic',
          sortable: false,
          customSort: false,
          hint: 'ระบุเป็นลิงก์รูปภาพ'
        },
        {
          text: 'Seats',
          value: 'seat_count',
          sortable: false,
          customSort: false,
          hint: 'ระบุเฉพาะตัวเลขเท่านั้น'
        },
        { text: '', value: '', sortable: false, customSort: false }
      ],
      filter: {
        seminar_id: '',
        seminar_name: '',
        seminar_date_from: '',
        seminar_date_to: '',
        start_time: '',
        end_time: '',
        location: '',
        seminar_status: 'all'
      },
      isExport: false,
      sortBy: 'seminar_id',
      orderBy: ORDER_BY.DESC,
      showImageDialog: false,
      selectedImage: null
    }
  },
  computed: {
    ...mapGetters(['selectedBot']),
    pageLength() {
      if (this.total) {
        const total = parseFloat(this.total)
        return Math.ceil(total / this.limit)
      } else {
        return 1
      }
    }
  },
  mounted() {
    this.handleFetchData()
  },
  methods: {
    async handleFetchData() {
      const vm = this
      try {
        vm.$store.dispatch('loading')
        const response = await vm.getSeminar()
        vm.$store.commit('CLEAR_LOADING')

        if (response && response.status === 200) {
          vm.dataItems = response.data.data
          vm.total = response.data.total
        }
      } catch (error) {
        vm.$store.commit('CLEAR_LOADING')
        console.error(error)
        return
      }
    },
    async getSeminar() {
      const vm = this
      try {
        const limit = vm.isExport ? vm.total : vm.limit
        const offset = vm.isExport ? 0 : vm.offset
        const data = {
          page_id: vm.selectedBot.pageid,
          limit: limit,
          offset: offset
        }

        if (vm.filter.seminar_id) {
          Object.assign(data, { seminar_id: vm.filter.seminar_id })
        }
        if (vm.filter.seminar_name) {
          Object.assign(data, { seminar_name: vm.filter.seminar_name })
        }
        if (vm.filter.seminar_date_from) {
          Object.assign(data, { datefrom: vm.filter.seminar_date_from })
          if (!vm.filter.seminar_date_to) {
            Object.assign(data, { dateto: vm.filter.seminar_date_from })
          }
        }
        if (vm.filter.seminar_date_to) {
          Object.assign(data, { dateto: vm.filter.seminar_date_to })
          if (!vm.filter.seminar_date_from) {
            Object.assign(data, { datefrom: vm.filter.seminar_date_to })
          }
        }
        if (vm.filter.start_time) {
          Object.assign(data, { start_time: vm.filter.start_time })
          if (!vm.filter.end_time) {
            Object.assign(data, { end_time: '23:59' })
          }
        }
        if (vm.filter.end_time) {
          Object.assign(data, { end_time: vm.filter.end_time })
          if (!vm.filter.start_time) {
            Object.assign(data, { start_time: '00:00' })
          }
        }
        if (vm.filter.location) {
          Object.assign(data, { location: vm.filter.location })
        }
        if (vm.filter.seminar_status !== 'all') {
          Object.assign(data, { seminar_status: vm.filter.seminar_status })
        }

        if (vm.sortBy) {
          let sortByKey = ''

          if (vm.sortBy === 'seminar_id') {
            sortByKey = 'sort_seminar_id'
          } else if (vm.sortBy === 'seminar_date') {
            sortByKey = 'sort_date'
          }

          Object.assign(data, { [sortByKey]: vm.orderBy })
        }

        vm.$store.dispatch('loading')
        const response = await mainAiraApi.getSeminar({
          config: data
        })
        vm.$store.commit('CLEAR_LOADING')
        handleResponse(response)

        if (response) {
          return response
        } else {
          return null
        }
      } catch (error) {
        vm.$store.commit('CLEAR_LOADING')
        handleApiError(error)
        return null
      }
    },
    transformHeaderField(data) {
      const result = []
      try {
        data.forEach((item) => {
          const tempItem = {
            seminar_id: '',
            seminar_name: '',
            seminar_date: '',
            start_time: '',
            end_time: '',
            description: '',
            details: '',
            location: '',
            seminar_pic: '',
            seats: ''
          }

          Object.keys(item).forEach((element) => {
            switch (element) {
              case 'Seminar ID':
                tempItem.seminar_id = item[element]
                break
              case 'Seminar Name':
                tempItem.seminar_name = item[element]
                break
              case 'Date':
                tempItem.seminar_date = item[element]
                break
              case 'Start Time':
                tempItem.start_time = item[element]
                break
              case 'End Time':
                tempItem.end_time = item[element]
                break
              case 'Description':
                tempItem.description = item[element]
                break
              case 'Detail':
                tempItem.details = item[element]
                break
              case 'Location':
                tempItem.location = item[element]
                break
              case 'Picture':
                tempItem.seminar_pic = item[element]
                break
              case 'Seat':
                tempItem.seats = item[element]
                break
              default:
              // code block
            }
          })
          result.push(tempItem)
        })
        return result
      } catch (error) {
        console.error(error)
        return []
      }
    },
    async handleImportSeminar(item) {
      const vm = this
      try {
        vm.$store.dispatch('loading')
        const response = await mainAiraApi.postImportSeminar({
          pageId: vm.selectedBot.pageid,
          data: {
            data: vm.transformHeaderField(item[0])
          }
        })
        vm.$store.commit('CLEAR_LOADING')
        handleResponse(response)

        if (response && response.status === 200) {
          await vm.$swal.fire({
            title: 'นำเข้ารายการสัมมนาสำเร็จ',
            text: 'หากต้องการเปิดสัมมนาให้ลูกค้าเห็น กรุณาเปิดสัมมนา',
            type: 'success'
          })
          vm.resetPagination()
        } else {
          await vm.$swal.fire({
            type: 'error',
            title: 'ไม่สามารถนำเข้ารายการสัมมนาได้',
            text: 'กรุณาตรวจสอบความถูกต้องของ format อีกครั้ง'
          })
        }
      } catch (error) {
        vm.$store.commit('CLEAR_LOADING')
        handleApiError(error)
        return
      }
    },
    downloadImportTemplate() {
      const data = []
      const heading = [
        [
          'Seminar ID',
          'Seminar Name',
          'Description',
          'Detail',
          'Date',
          'Start Time',
          'End Time',
          'Location',
          'Picture',
          'Seat'
        ]
      ]

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet([])
      XLSX.utils.sheet_add_aoa(ws, heading)
      XLSX.utils.sheet_add_json(ws, data)
      XLSX.utils.book_append_sheet(wb, ws)
      XLSX.writeFile(wb, `template-import-seminar.xlsx`)
    },
    async handleExportXlsx() {
      const vm = this
      try {
        vm.isExport = true
        const response = await vm.getSeminar()
        const requestData = response.data.data
        vm.isExport = false

        if (requestData == null) return
        const requests = requestData.map((el) => {
          return {
            seminar_status: el.seminar_status ? 'active' : 'inactive',
            seminar_id: el.seminar_id,
            seminar_name: el.seminar_name,
            description: el.description,
            details: el.details,
            seminar_date: el.seminar_date ? dayjs(el.seminar_date).format('DD/MM/YYYY') : '-',
            time: `${vm.getTimeFormat(el.start_time)} - ${vm.getTimeFormat(el.end_time)}`,
            location: el.location,
            seminar_pic: el.seminar_pic,
            seat: el.seats,
            seat_count: el.seat_count
          }
        })

        const heading = [
          [
            'Status',
            'Seminar ID',
            'Seminar Name',
            'Description',
            'Detail',
            'Date',
            'Time',
            'Location',
            'Picture',
            'Seat',
            'Booking Seat'
          ]
        ]

        const wb = XLSX.utils.book_new()
        const ws = XLSX.utils.json_to_sheet([])
        XLSX.utils.sheet_add_aoa(ws, heading)
        XLSX.utils.sheet_add_json(ws, requests, { origin: 'A2', skipHeader: true })
        XLSX.utils.book_append_sheet(wb, ws)
        XLSX.writeFile(wb, `seminar-list.xlsx`)
      } catch (error) {
        console.error(error)
      }
    },
    handleCloseSearch() {
      this.showAdvancedSearch = false
    },
    handleChangePage(val) {
      const vm = this
      if (vm.pagetmp !== val) {
        vm.pagetmp = val
        vm.offset = vm.pageNumber * vm.limit - vm.limit
        vm.handleFetchData()
      }
    },
    handleSearch() {
      this.resetPagination()
    },
    handleClearSearch() {
      this.filter = {
        seminar_id: '',
        seminar_name: '',
        seminar_date: '',
        start_time: '',
        end_time: '',
        location: '',
        seminar_status: ''
      }
      this.resetPagination()
      this.handleCloseSearch()
    },
    resetPagination() {
      this.offset = 0
      this.pagetmp = 1
      this.pageNumber = 1
      this.handleFetchData()
    },
    handleChangeSort() {
      this.offset = 0
      this.pagetmp = 1
      this.pageNumber = 1
      this.handleFetchData()
    },
    handleShowImage(url) {
      this.selectedImage = url
      this.showImageDialog = true
    },
    getTimeFormat(val) {
      let time = ''
      if (val) {
        const arrTime = val.split(':')
        time = `${arrTime[0]}:${arrTime[1]}`
      }
      return time
    },

    async handleChangeStatus(id, status) {
      const vm = this
      try {
        vm.$store.dispatch('loading')
        const response = await mainAiraApi.putUpdateSeminar({
          data: {
            status: !status
          },
          id: id,
          pageId: vm.selectedBot.pageid
        })
        vm.$store.commit('CLEAR_LOADING')
        handleResponse(response)

        if (response && response.status === 200) {
          vm.handleFetchData()
        }
      } catch (error) {
        vm.$store.commit('CLEAR_LOADING')
        handleApiError(error)
        return
      }
    },
    async handleDeleteSeminar(id) {
      const vm = this
      try {
        const result = await vm.$swal.fire({
          type: 'warning',
          title: 'ยืนยันการลบสัมมนานี้หรือไม่',
          text: 'หากลบสัมมนานี้ ข้อมูลการจองของลูกค้าจะหายไปด้วย',
          showCancelButton: true,
          cancelButtonText: vm.$t('cancel'),
          confirmButtonText: vm.$t('OK'),
          reverseButtons: true
        })

        if (result.value) {
          vm.$store.dispatch('loading')
          const response = await vm.deleteSeminar(id)
          vm.$store.commit('CLEAR_LOADING')

          if (response && response.status === 200) {
            await this.$swal.fire({
              text: 'สำเร็จ',
              type: 'success'
            })
            vm.resetPagination()
          }
        }
      } catch (error) {
        vm.$store.commit('CLEAR_LOADING')
        console.error(error)
      }
    },
    async deleteSeminar(id) {
      const vm = this
      try {
        vm.$store.dispatch('loading')
        const response = await mainAiraApi.deleteSeminar({
          pageId: vm.selectedBot.pageid,
          id: id
        })
        vm.$store.commit('CLEAR_LOADING')
        handleResponse(response)

        if (response) {
          return response
        } else {
          return null
        }
      } catch (error) {
        vm.$store.commit('CLEAR_LOADING')
        handleApiError(error)
        return null
      }
    },
    handleRedirectToBooking(id) {
      this.$router.push({ path: 'aira', name: 'booking-list', query: { id: id } })
    }
  }
}
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/view/template/Aira/SeminarManagement.vue